import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { useWebSocket } from '../../shared/hooks/useWebSocket';

import { LIVE_ALERTS_QUERY_KEY } from './useLiveAlertsQuery';
import { RIDE_DETAILS_QUERY_KEY } from './useRideDetailsQuery';
import { RIDE_NOTES_QUERY_KEY } from './useRideNotesQuery';
import { RIDE_OVERVIEW_QUERY_KEY } from './useRideOverviewQuery';

const EVENTS_TOPIC = 'events';

type Message = {
  topic: string;
  data: { eventName: string; eventAttributes: string; newAttributeValue: string }[];
};

interface IUseLiveRideUpdates {
  rideUuid: string;
  rideSlug: string;
  rideState: string;
}

export const useLiveRideUpdates = ({ rideUuid, rideSlug, rideState }: IUseLiveRideUpdates) => {
  const queryClient = useQueryClient();

  const { lastJsonMessage: rideStateChanged } = useWebSocket<Message>(
    {
      topic: EVENTS_TOPIC,
      resource_uuid: rideUuid,
      resource_type: 'trip',
    },
    { reconnectAttempts: 10 },
    !!rideUuid
  );

  useEffect(() => {
    // this is to ensure that the ride details, overview, live alerts, and notes are updated
    // when the ride state changes or the start time changes
    if (
      (rideStateChanged?.data?.[0]?.eventName === 'state_changed' &&
        rideStateChanged?.data?.[0]?.newAttributeValue !== rideState) ||
      rideStateChanged?.data?.[0]?.eventName === 'starts_at_changed'
    ) {
      queryClient.invalidateQueries({ queryKey: [RIDE_DETAILS_QUERY_KEY, rideSlug] });
      queryClient.invalidateQueries({ queryKey: [RIDE_OVERVIEW_QUERY_KEY, rideSlug] });
      queryClient.invalidateQueries({ queryKey: [LIVE_ALERTS_QUERY_KEY, rideSlug] });
      queryClient.invalidateQueries({ queryKey: [RIDE_NOTES_QUERY_KEY, rideSlug] });
    }
  }, [queryClient, rideSlug, rideStateChanged]);
};
